import React from 'react';
import { motion } from 'framer-motion';
import { UserX, ShoppingCart, Clock, MessageSquareX, HelpCircle, ArrowRight, Cpu, Bot } from 'lucide-react';

const PROBLEM_IMAGE = '/assets/problem-section.jpg';

const ProblemSection = () => {
  const painPoints = [
    { icon: UserX, text: 'Visitors leave without buying', color: 'bg-red-50 border-red-200 text-red-600' },
    { icon: ShoppingCart, text: 'Cart abandonment is high', color: 'bg-orange-50 border-orange-200 text-orange-600' },
    { icon: HelpCircle, text: 'Repetitive support queries', color: 'bg-amber-50 border-amber-200 text-amber-600' },
    { icon: MessageSquareX, text: 'No personalized recommendations', color: 'bg-yellow-50 border-yellow-200 text-yellow-600' },
    { icon: Clock, text: 'Delayed customer responses', color: 'bg-lime-50 border-lime-200 text-lime-600' },
  ];

  return (
    <section className="py-16 sm:py-24 md:py-32 bg-white" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left - Problem Statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold tracking-widest uppercase text-red-600 mb-4 bg-red-50 px-3 sm:px-4 py-2 rounded-full border border-red-200">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              The Problem
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight mb-4 sm:mb-6">
              Your Store Gets Traffic. But Who Converts It at{' '}
              <span className="text-red-500">2 AM</span>?
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-slate-600 leading-relaxed mb-6 sm:mb-8">
              Every minute a visitor spends without AI engagement is a lost opportunity. Your competitors are already using intelligent automation.
            </p>
            
            {/* Pain Points */}
            <div className="space-y-3 sm:space-y-4">
              {painPoints.map((point, index) => (
                <motion.div
                  key={point.text}
                  className={`flex items-center gap-3 sm:gap-4 rounded-xl sm:rounded-2xl p-3 sm:p-4 border ${point.color}`}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center bg-white shadow-sm">
                    <point.icon className="w-5 sm:w-6 h-5 sm:h-6" />
                  </div>
                  <span className="text-slate-700 font-semibold text-sm sm:text-lg">{point.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right - Image with Solution Teaser overlay */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative"
          >
            {/* Main Image */}
            <img 
              src={PROBLEM_IMAGE}
              alt="E-commerce store owner managing orders"
              className="rounded-2xl sm:rounded-3xl shadow-xl w-full h-[320px] sm:h-[450px] lg:h-[520px] object-cover border-2 border-slate-100"
              data-testid="problem-image"
            />

            {/* Overlay Solution Card */}
            <motion.div 
              className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border border-slate-200"
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 sm:w-14 h-12 sm:h-14 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Bot className="w-6 sm:w-7 h-6 sm:h-7 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-extrabold text-slate-900">AI That Never Sleeps</h3>
                  <p className="text-xs sm:text-sm text-slate-500">Arismate AI agents close sales 24/7 on autopilot</p>
                </div>
              </div>
            </motion.div>

            {/* Floating 24/7 Badge */}
            <motion.div 
              className="absolute -top-3 -right-2 sm:-right-4 bg-teal-600 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-lg flex items-center gap-2"
              animate={{ y: [0, -5, 0] }}
              transition={{ repeat: Infinity, duration: 2.5 }}
            >
              <Cpu className="w-4 h-4" />
              24/7 Active
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
